import { useEffect, useRef, useState, type RefObject } from 'react';
import { deviceMaxWidths, TEMPLATE_WIDTH, type PreviewSize } from '../constants';

// scales the template preview down to fit the available space: the outer scroll
// container is observed for its size, the template's own natural height
// is observed separately, and the smaller of the width-fit and height-fit ratios wins
export function usePreviewScale(
  previewSize: PreviewSize,
  templateContainer: RefObject<HTMLDivElement | null>,
  templateComponent: unknown,
  event: unknown,
  theme: unknown,
  isDragActive?: boolean
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [templateNaturalHeight, setTemplateNaturalHeight] = useState(0);
  const [scale, setScale] = useState(1);
  // freezes height tracking during a drag so the preview doesn't rescale on every hover
  const isDragActiveRef = useRef(isDragActive);
  useEffect(() => {
    isDragActiveRef.current = isDragActive;
  }, [isDragActive]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) => {
      setContainerSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const template = templateContainer.current;
    if (!template) return;
    const observer = new ResizeObserver(([entry]) => {
      if (isDragActiveRef.current) return;
      setTemplateNaturalHeight(entry.contentRect.height);
    });
    observer.observe(template);
    return () => observer.disconnect();
  }, [templateContainer, templateComponent, event, theme]);

  useEffect(() => {
    if (!containerSize.width || !containerSize.height || !templateNaturalHeight) return;

    const maxWidth = deviceMaxWidths[previewSize];
    const availableWidth = Math.min(containerSize.width, maxWidth);
    const widthScale = (availableWidth * 0.6) / TEMPLATE_WIDTH;

    setScale(Math.min(1, widthScale));
  }, [containerSize, templateNaturalHeight, previewSize]);

  return { containerRef, scale };
}
